import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Modal, Button, Form, Row, Col, Alert, Table, InputGroup, Pagination, Badge,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import transactionTikaneSenpService from '../services/transactionTikaneSenpService';
import tikaneSenpService from '../services/tikaneSenpService';
import { AuthContext } from '../context/AuthContext';
import ReceiptPrintButton from './ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

export default function TransactionTiKaneSenpModal({ tiKaneSenpId, show, onHide }) {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('TRANS_VIEW');
  const canDeposit = hasRight('TRANS_DEPOSIT');
  const canWithdraw = hasRight('TRANS_WITHDRAW');
  const initialDates = useMemo(() => defaultDateRange(), []);

  const [account, setAccount] = useState(null);
  const [accountNumber, setAccountNumber] = useState('');
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opError, setOpError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: { type: 'DEPOSIT', amount: '', description: '' },
  });
  const selectedType = watch('type', 'DEPOSIT');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const isClosed = String(account?.status || '').toUpperCase() === 'CLOSED';

  const fetchData = useCallback(async (targetPage = page) => {
    if (!tiKaneSenpId || !show || !canView) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) { setLoadError(dateError); setTransactions([]); setTotal(0); return; }
    setLoading(true); setLoadError('');
    try {
      const [acc, txData] = await Promise.all([
        tikaneSenpService.get(tiKaneSenpId),
        transactionTikaneSenpService.listByAccount(tiKaneSenpId, {
          page: targetPage, pageSize, search: filter, type: typeFilter, dateFrom, dateTo,
        }),
      ]);
      setAccount(acc || null);
      setAccountNumber(acc?.accountNumber || txData?.accountNumber || '');
      setBalance(typeof txData?.balance !== 'undefined' ? Number(txData.balance) : Number(acc?.balance || 0));
      setTransactions(txData?.transactions || []);
      setTotal(Number(txData?.total || 0));
      const pages = Math.max(1, Number(txData?.totalPages || Math.ceil(Number(txData?.total || 0) / pageSize)));
      if (targetPage > pages) setPage(pages);
    } catch (e) {
      setLoadError(e?.response?.data?.message || 'Erreur lors du chargement des transactions.');
    } finally { setLoading(false); }
  }, [canView, dateFrom, dateTo, filter, page, pageSize, show, tiKaneSenpId, typeFilter]);

  useEffect(() => { if (show) fetchData(page); }, [show, page, fetchData]);
  useEffect(() => { if (show) setPage(1); }, [filter, typeFilter, dateFrom, dateTo, pageSize, show]);
  useEffect(() => {
    if (!show) {
      setFilter(''); setTypeFilter(''); setPage(1); setTotal(0);
      const dates = defaultDateRange(); setDateFrom(dates.dateFrom); setDateTo(dates.dateTo);
      reset({ type: 'DEPOSIT', amount: '', description: '' });
      setOpError(''); setLoadError('');
    }
  }, [show, reset]);

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2); const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const buildReceiptDocument = (tx) => {
    const username = tx?.agent?.username || '—';
    const holder = account?.client
      ? `${account.client.firstName || ''} ${account.client.lastName || ''}`.trim()
      : account?.minor
        ? `${account.minor.firstName || ''} ${account.minor.lastName || ''}`.trim()
        : '—';
    return {
      title: `Reçu Kanè epay${tx?.codeTransaction ? ` - ${tx.codeTransaction}` : ''}`,
      rows: [
        { label: 'Compte', value: accountNumber || '—' }, { label: 'Titulaire', value: holder },
        { label: 'Statut compte', value: account?.status || '—' }, { label: 'ID transaction', value: tx?.id || '—' },
        { label: 'Code transaction', value: tx?.codeTransaction || '—' },
        { label: 'Type', value: tx?.type === 'DEPOSIT' ? 'Dépôt' : tx?.type === 'WITHDRAW' ? 'Retrait' : (tx?.type || '—') },
        { label: 'Montant', value: `${Number(tx?.amount || 0).toFixed(2)} HTG` },
        { label: 'Solde après', value: `${Number(tx?.balanceAfter || 0).toFixed(2)} HTG` },
        { label: 'Utilisateur', value: username }, { label: 'Date', value: tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : '—' },
        { label: 'Note', value: tx?.description || '—' },
      ],
      signatures: { client: 'Signature client', cashier: username },
    };
  };

  const onSubmit = async (form) => {
    if (!tiKaneSenpId) return;
    setOpError('');
    if (isClosed) { setOpError('Ce compte Ti Kanè Senp est clôturé. Aucune transaction ne peut être effectuée.'); return; }
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) { setOpError('Montant invalide.'); return; }
    try {
      if (form.type === 'DEPOSIT') {
        await transactionTikaneSenpService.deposit(tiKaneSenpId, { amount, description: form.description || '' });
      } else {
        await transactionTikaneSenpService.withdraw(tiKaneSenpId, { amount, description: form.description || '' });
      }
      reset({ type: form.type, amount: '', description: '' });
      setPage(1); await fetchData(1);
    } catch (e) { setOpError(e?.response?.data?.message || "Erreur lors de l'opération."); }
  };

  const holder = account?.client
    ? `${account.client.firstName || ''} ${account.client.lastName || ''}`.trim()
    : account?.minor
      ? `${account.minor.firstName || ''} ${account.minor.lastName || ''}`.trim()
      : '—';

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>{`Opérations Ti Kanè Senp – Compte ${accountNumber || tiKaneSenpId || ''}`}</Modal.Title></Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="danger">{loadError}</Alert>}
        {loading && <Alert variant="light" className="py-2">Chargement…</Alert>}
        <Row className="mb-3"><Col md={4}><strong>Solde actuel :</strong> {Number(balance || 0).toFixed(2)}</Col><Col md={4}><strong>Statut :</strong> <Badge bg={isClosed ? 'secondary' : 'success'}>{account?.status || 'ACTIVE'}</Badge></Col><Col md={4}><strong>Titulaire :</strong> {holder}</Col></Row>

        {(canDeposit || canWithdraw) && <>
          <h6>Nouvelle opération</h6>
          {isClosed && <Alert variant="warning">Ce compte est clôturé. Les dépôts et retraits sont bloqués.</Alert>}
          {opError && <Alert variant="danger">{opError}</Alert>}
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="g-3 align-items-end">
              <Col md={3}><Form.Group><Form.Label>Type</Form.Label><Form.Select {...register('type')} disabled={isClosed}>{canDeposit && <option value="DEPOSIT">Dépôt</option>}{canWithdraw && <option value="WITHDRAW">Retrait</option>}</Form.Select></Form.Group></Col>
              <Col md={3}><Form.Group><Form.Label>Montant *</Form.Label><Form.Control type="number" step="0.01" min="0" disabled={isClosed} {...register('amount', { required: 'Requis', min: { value: 0.01, message: '> 0' } })} />{errors.amount && <Form.Text className="text-danger">{errors.amount.message}</Form.Text>}</Form.Group></Col>
              <Col md={4}><Form.Group><Form.Label>Description</Form.Label><Form.Control {...register('description')} disabled={isClosed} /></Form.Group></Col>
              <Col md={2}><Button type="submit" className="w-100" disabled={isClosed || (selectedType === 'DEPOSIT' ? !canDeposit : !canWithdraw)}>Valider</Button></Col>
            </Row>
          </Form>
        </>}

        {canView && <>
          <hr className="my-4" /><h6>Historique des transactions</h6>
          <Row className="g-2 align-items-end mb-3">
            <Col md={2}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
            <Col md={2}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
            <Col md={4}><Form.Label>Recherche</Form.Label><InputGroup><Form.Control placeholder="Type, note, code..." value={filter} onChange={(e) => setFilter(e.target.value)} /><Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button></InputGroup></Col>
            <Col md={2}><Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tous types</option><option value="DEPOSIT">Dépôt</option><option value="WITHDRAW">Retrait</option></Form.Select></Col>
            <Col md={2}><Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 / page</option><option value={25}>25 / page</option><option value={50}>50 / page</option></Form.Select></Col>
          </Row>
          <Table hover responsive><thead><tr><th>Date</th><th>Type</th><th>Montant</th><th>Solde après</th><th>Code</th><th>Utilisateur</th><th>Reçu</th></tr></thead><tbody>
            {transactions.map((t) => <tr key={t.id}><td>{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</td><td>{t.type === 'DEPOSIT' ? 'Dépôt' : 'Retrait'}</td><td>{Number(t.amount || 0).toFixed(2)}</td><td>{Number(t.balanceAfter || 0).toFixed(2)}</td><td>{t.codeTransaction || '—'}</td><td>{t.agent?.username || '—'}</td><td><ReceiptPrintButton getDocument={() => buildReceiptDocument(t)} /></td></tr>)}
            {transactions.length === 0 && <tr><td colSpan={7} className="text-center text-muted">Aucune transaction pour cette période.</td></tr>}
          </tbody></Table>
          <div className="d-flex justify-content-between align-items-center"><span className="text-muted">Résultats : {total} • Page {page}/{totalPages}</span><Pagination className="mb-0">{paginationItems}</Pagination></div>
        </>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fermer</Button></Modal.Footer>
    </Modal>
  );
}
